import { FlowRouterContext } from 'nove8/contexts/flowRouterContext'
import { saveUserAnswer } from 'nove8/helpers/api/saveUserAnswer'
import { getDogProblemFromSavedAnswers } from 'nove8/helpers/getDogProblemFromSavedAnswers'
import { getSelectedAnswers } from 'nove8/helpers/getSelectedAnswers'
import { updateUserPropertiesWithAnswer } from 'nove8/helpers/updateUserPropertiesWithAnswer'
import { useGetSavedAnswers } from 'nove8/hooks/useGetSavedAnswers'
import { usePageRequiredAction } from 'nove8/hooks/usePageRequiredAction'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'
import { useZustandStore } from 'zustandGlobalStore'
import { FlowButton } from '../../components/FlowButton'
import { OptionType } from '../../components/Option'
import { QuestionButton } from '../../components/QuestionButton'
import { StyledBreedFrequency as S } from './TriggersQuestion.styles'

const QUESTION = 'What usually happens during walks?'

export const CALM_OPTION = 'Walks are usually calm'
export const IGNORES_COMMANDS_OPTION = 'Ignores commands'
export const GETS_OVERLY_EXCITED_OPTION = 'Gets overly excited'
export const REACTS_TO_PEOPLE_OR_DOGS_OPTION = 'Reacts to people or dogs'

const ANSWERS_OPTIONS = [
  {
    value: IGNORES_COMMANDS_OPTION,
    title: 'onboarding.walks.ignoresCommandsOption',
    testId: 'quiz-during-walk-ignores-commands',
    emoji: '👀',
  },
  {
    value: GETS_OVERLY_EXCITED_OPTION,
    title: 'onboarding.walks.getsOverlyExcitedOption',
    testId: 'quiz-during-walk-gets-overly-excited',
    emoji: '🤪',
  },
  {
    value: REACTS_TO_PEOPLE_OR_DOGS_OPTION,
    title: 'onboarding.walks.reactsToPeopleOrDogsOption',
    testId: 'quiz-during-walk-reacts-to-people-or-dogs',
    emoji: '🐶',
  },
  {
    value: CALM_OPTION,
    title: 'onboarding.walks.calmOption',
    testId: 'quiz-during-walk-calm',
    emoji: '😌',
  },
]

export const DuringWalkQuestion = () => {
  const { t } = useTranslation()
  const { onNextStep } = useContext(FlowRouterContext)

  const { currentPageId, currentPageNumber } = useZustandStore(
    useShallow((state) => ({
      currentPageId: state.currentPageId,
      currentPageNumber: state.currentPageNumber,
    })),
  )

  const savedAnswers = useGetSavedAnswers()

  const selectedAnswers = useMemo(
    () => getSelectedAnswers(savedAnswers, currentPageId),
    [savedAnswers, currentPageId],
  )

  const [answers, setAnswers] = useState<string[]>([])

  const handleContinue = useCallback(
    (isNoneAnswer: boolean) => {
      if (!currentPageId || !currentPageNumber) return

      const currentAnswers = isNoneAnswer ? 'none' : answers
      saveUserAnswer({
        question: QUESTION,
        answers: currentAnswers,
        pageName: currentPageId,
        pageNumber: currentPageNumber,
      })

      const updatedAnswers = {
        ...savedAnswers,
        [currentPageId]: currentAnswers,
      }
      const dogProblem = getDogProblemFromSavedAnswers(updatedAnswers) || 'none'
      updateUserPropertiesWithAnswer({ dog_problem: dogProblem })

      onNextStep()
    },
    [answers, savedAnswers, currentPageId, currentPageNumber, onNextStep],
  )

  const optionProps = useMemo(
    () => ({
      ...(currentPageId && { name: currentPageId }),
      type: OptionType.CHECKBOX,
      onChange: (value: string, isChecked: boolean) => {
        setAnswers((prev) => {
          if (value === CALM_OPTION && isChecked) {
            return [CALM_OPTION]
          }
          if (value === CALM_OPTION && !isChecked) {
            return prev.filter((item) => item !== CALM_OPTION)
          }
          if (prev.includes(CALM_OPTION)) {
            return [value]
          }
          if (isChecked) {
            return [...prev, value]
          }
          return prev.filter((item) => item !== value)
        })
      },
    }),
    [currentPageId],
  )

  useEffect(() => {
    setAnswers(selectedAnswers as string[])
  }, [selectedAnswers])

  usePageRequiredAction()

  return (
    <S.Wrapper>
      <S.Content>
        <S.Title>{t`onboarding.walks.question`}</S.Title>

        <S.OptionsContainer data-testid="quiz-dog-during-walks">
          {ANSWERS_OPTIONS.map((item) => (
            <S.OptionVariant2
              {...optionProps}
              value={item.value}
              data-testid={item.testId}
              key={item.value}
              checked={Array.isArray(answers) && answers.includes(item.value)}
            >
              <QuestionButton>
                <S.OptionContentContainer>
                  <S.OptionEmoji>{item.emoji}</S.OptionEmoji>
                  <S.OptionTitle>{t(item.title)}</S.OptionTitle>
                </S.OptionContentContainer>
              </QuestionButton>
            </S.OptionVariant2>
          ))}
        </S.OptionsContainer>

        <S.StickyButtonContainer>
          <S.ButtonContainer>
            <FlowButton
              isButtonDisabled={!answers.length}
              onClick={() => handleContinue(false)}
              testId="quiz-continue-button"
            />
          </S.ButtonContainer>

          <S.TextButton
            data-testid="quiz-option-none"
            onClick={() => handleContinue(true)}
          >
            {t`onboarding.common.noneOfTheAbove`}
          </S.TextButton>
        </S.StickyButtonContainer>
      </S.Content>
    </S.Wrapper>
  )
}
